import { Component, Input, Output, EventEmitter, ChangeDetectorRef, ElementRef, ViewChild, AfterViewChecked, SecurityContext, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LogError, Metadata, RunView } from '@memberjunction/core';
import { GraphQLDataProvider } from '@memberjunction/graphql-dataprovider';
import { marked } from 'marked';

interface ChatMessage {
    role: 'user' | 'assistant' | 'system';
    content: string;
    rendered?: SafeHtml;
    timestamp: Date;
    error?: boolean;
}

const SUPPORT_AGENT_NAME = 'Marketing CMO Support Agent';

@Component({
    standalone: false,
    selector: 'mkt-cmo-support-chat',
    template: `
<div class="cmo-chat" [class.cmo-chat--open]="open">
    <button class="cmo-chat__fab" *ngIf="!open" (click)="onOpen()" title="Ask the CMO Support Agent">
        <i class="fa-solid fa-message"></i>
        <span>Ask the agent</span>
    </button>

    <aside class="cmo-chat__panel" *ngIf="open">
        <header class="cmo-chat__header">
            <div class="cmo-chat__header-title">
                <i class="fa-solid fa-robot"></i>
                <div>
                    <div class="cmo-chat__header-name">CMO Support</div>
                    <div class="cmo-chat__header-sub">Read-only assistant · {{ brandName || 'company' }}</div>
                </div>
            </div>
            <button class="cmo-chat__close" (click)="onClose()" title="Close"><i class="fa-solid fa-xmark"></i></button>
        </header>

        <div class="cmo-chat__messages" #scroll>
            <div *ngIf="messages.length === 0 && !sending" class="cmo-chat__welcome">
                <p>Ask about your marketing performance, content, or strategy. The agent has read-only access — it can show you data but not change it.</p>
                <div class="cmo-chat__suggestions">
                    <button class="cmo-chat__suggestion" (click)="useSuggestion(s)" *ngFor="let s of suggestions">{{ s }}</button>
                </div>
            </div>

            <div *ngFor="let m of messages" class="cmo-chat__msg" [class.cmo-chat__msg--user]="m.role === 'user'" [class.cmo-chat__msg--assistant]="m.role === 'assistant'" [class.cmo-chat__msg--error]="m.error">
                <div class="cmo-chat__msg-avatar">
                    <i class="fa-solid" [class.fa-user]="m.role === 'user'" [class.fa-robot]="m.role === 'assistant'"></i>
                </div>
                <div class="cmo-chat__msg-bubble">
                    <div *ngIf="m.role === 'assistant' && m.rendered" [innerHTML]="m.rendered"></div>
                    <div *ngIf="m.role !== 'assistant' || !m.rendered" class="cmo-chat__msg-text">{{ m.content }}</div>
                </div>
            </div>

            <div *ngIf="sending" class="cmo-chat__msg cmo-chat__msg--assistant">
                <div class="cmo-chat__msg-avatar"><i class="fa-solid fa-robot"></i></div>
                <div class="cmo-chat__msg-bubble">
                    <div class="cmo-chat__typing">
                        <span></span><span></span><span></span>
                        <small>{{ statusText }}</small>
                    </div>
                </div>
            </div>
        </div>

        <form class="cmo-chat__input" (submit)="onSubmit($event)">
            <textarea
                [(ngModel)]="draft"
                name="draft"
                rows="2"
                placeholder="Ask anything about marketing..."
                [disabled]="sending"
                (keydown.enter)="onEnterKey($event)"
            ></textarea>
            <button type="submit" class="cmo-chat__send" [disabled]="sending || !draft.trim()">
                <i class="fa-solid" [class.fa-paper-plane]="!sending" [class.fa-spinner]="sending" [class.fa-spin]="sending"></i>
            </button>
        </form>
    </aside>
</div>
    `,
    styles: [`
        :host { display: block; position: fixed; bottom: 24px; right: 24px; z-index: 1000; font-family: inherit; }

        .cmo-chat__fab { display: inline-flex; align-items: center; gap: 6px; padding: 10px 16px; border: 1px solid var(--mj-brand-primary, #6366f1); border-radius: 6px; background: var(--mj-brand-primary, #6366f1); color: var(--mj-text-inverse, #fff); font-size: 13px; font-weight: 600; cursor: pointer; box-shadow: 0 4px 14px rgba(99,102,241,0.3); transition: filter 0.15s, transform 0.15s; font-family: inherit; }
        .cmo-chat__fab:hover { filter: brightness(1.1); transform: translateY(-1px); }
        .cmo-chat__fab i { font-size: 13px; }

        .cmo-chat__panel { width: 420px; height: 600px; max-height: calc(100vh - 48px); display: flex; flex-direction: column; background: var(--mj-bg-surface, #fff); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 10px; box-shadow: 0 16px 48px rgba(0,0,0,0.14); overflow: hidden; }

        .cmo-chat__header { display: flex; align-items: center; justify-content: space-between; padding: 14px 18px; background: var(--mj-brand-primary, #6366f1); color: var(--mj-text-inverse, #fff); }
        .cmo-chat__header-title { display: flex; align-items: center; gap: 12px; }
        .cmo-chat__header-title > i { font-size: 22px; }
        .cmo-chat__header-name { font-size: 14px; font-weight: 700; }
        .cmo-chat__header-sub { font-size: 11px; opacity: 0.85; margin-top: 2px; }
        .cmo-chat__close { background: rgba(255,255,255,0.15); border: none; color: #fff; width: 28px; height: 28px; border-radius: 50%; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background 0.15s; }
        .cmo-chat__close:hover { background: rgba(255,255,255,0.25); }

        .cmo-chat__messages { flex: 1; overflow-y: auto; padding: 16px; display: flex; flex-direction: column; gap: 12px; background: var(--mj-bg-page, #f7f7fa); }

        .cmo-chat__welcome { padding: 16px 4px; color: var(--mj-text-secondary, #666); }
        .cmo-chat__welcome p { margin: 0 0 12px 0; font-size: 13px; line-height: 1.5; }
        .cmo-chat__suggestions { display: flex; flex-direction: column; gap: 6px; }
        .cmo-chat__suggestion { text-align: left; background: var(--mj-bg-surface, #fff); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 8px; padding: 8px 12px; font-size: 12px; color: var(--mj-text-primary, #1a1a1a); cursor: pointer; transition: border-color 0.15s, background 0.15s; }
        .cmo-chat__suggestion:hover { border-color: var(--mj-brand-primary, #6366f1); background: color-mix(in srgb, var(--mj-brand-primary, #6366f1) 5%, var(--mj-bg-surface, #fff)); }

        .cmo-chat__msg { display: flex; gap: 10px; max-width: 92%; }
        .cmo-chat__msg--user { align-self: flex-end; flex-direction: row-reverse; }
        .cmo-chat__msg-avatar { width: 28px; height: 28px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 12px; }
        .cmo-chat__msg--assistant .cmo-chat__msg-avatar { background: var(--mj-brand-primary, #6366f1); color: var(--mj-text-inverse, #fff); }
        .cmo-chat__msg--user .cmo-chat__msg-avatar { background: var(--mj-text-secondary, #71717a); color: var(--mj-text-inverse, #fff); }
        .cmo-chat__msg-bubble { padding: 10px 14px; border-radius: 12px; font-size: 13px; line-height: 1.5; max-width: 100%; word-wrap: break-word; }
        .cmo-chat__msg--user .cmo-chat__msg-bubble { background: var(--mj-brand-primary, #6366f1); color: var(--mj-text-inverse, #fff); border-bottom-right-radius: 4px; }
        .cmo-chat__msg--assistant .cmo-chat__msg-bubble { background: var(--mj-bg-surface, #fff); color: var(--mj-text-primary, #1a1a1a); border: 1px solid var(--mj-border-default, #e0e0e0); border-bottom-left-radius: 4px; }
        .cmo-chat__msg--error .cmo-chat__msg-bubble { background: color-mix(in srgb, var(--mj-status-error, #ef4444) 10%, var(--mj-bg-surface, #fff)); border-color: var(--mj-status-error, #ef4444); color: var(--mj-status-error, #ef4444); }
        .cmo-chat__msg-text { white-space: pre-wrap; }
        .cmo-chat__msg-bubble :first-child { margin-top: 0; }
        .cmo-chat__msg-bubble :last-child { margin-bottom: 0; }
        .cmo-chat__msg-bubble p { margin: 6px 0; }
        .cmo-chat__msg-bubble ul, .cmo-chat__msg-bubble ol { margin: 6px 0; padding-left: 20px; }

        .cmo-chat__typing { display: flex; align-items: center; gap: 4px; }
        .cmo-chat__typing span { width: 6px; height: 6px; border-radius: 50%; background: var(--mj-text-secondary, #666); animation: cmo-pulse 1.4s ease-in-out infinite; }
        .cmo-chat__typing span:nth-child(2) { animation-delay: 0.2s; }
        .cmo-chat__typing span:nth-child(3) { animation-delay: 0.4s; }
        .cmo-chat__typing small { margin-left: 8px; font-size: 11px; color: var(--mj-text-secondary, #666); }
        @keyframes cmo-pulse { 0%, 80%, 100% { opacity: 0.3; transform: scale(0.7); } 40% { opacity: 1; transform: scale(1); } }

        .cmo-chat__input { display: flex; gap: 8px; padding: 12px 14px; border-top: 1px solid var(--mj-border-default, #e0e0e0); background: var(--mj-bg-surface, #fff); }
        .cmo-chat__input textarea { flex: 1; resize: none; border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 8px; padding: 8px 10px; font-family: inherit; font-size: 13px; color: var(--mj-text-primary, #1a1a1a); outline: none; transition: border-color 0.15s; }
        .cmo-chat__input textarea:focus { border-color: var(--mj-brand-primary, #6366f1); }
        .cmo-chat__input textarea:disabled { background: var(--mj-bg-page, #f5f5f5); }
        .cmo-chat__send { background: var(--mj-brand-primary, #6366f1); color: var(--mj-text-inverse, #fff); border: none; width: 40px; border-radius: 6px; cursor: pointer; transition: filter 0.15s, opacity 0.15s; flex-shrink: 0; }
        .cmo-chat__send:hover:not(:disabled) { filter: brightness(1.1); }
        .cmo-chat__send:disabled { opacity: 0.5; cursor: not-allowed; }
    `]
})
export class CMOSupportChatComponent implements AfterViewChecked {
    @Input() companyId: string = '';
    @Input() brandName: string = '';

    @Output() opened = new EventEmitter<void>();
    @Output() closed = new EventEmitter<void>();

    @ViewChild('scroll') private scrollRef?: ElementRef<HTMLDivElement>;
    private cdr = inject(ChangeDetectorRef);
    private sanitizer = inject(DomSanitizer);

    open = false;
    sending = false;
    statusText = 'Thinking…';
    draft = '';
    messages: ChatMessage[] = [];
    private sessionId: string = '';
    private agentId: string | null = null;
    private shouldScroll = false;

    suggestions = [
        'What blogs published in the last 30 days?',
        'Which content pillar is performing best?',
        'Show me open recommendations.',
        'Summarize this week\'s social posts.',
    ];

    onOpen(): void {
        this.open = true;
        if (!this.sessionId) this.sessionId = `cmo-support-${Date.now()}`;
        this.opened.emit();
        this.cdr.detectChanges();
    }

    onClose(): void {
        this.open = false;
        this.closed.emit();
        this.cdr.detectChanges();
    }

    useSuggestion(s: string): void {
        this.draft = s;
    }

    onEnterKey(event: Event): void {
        const ke = event as KeyboardEvent;
        if (ke.shiftKey) return;
        ke.preventDefault();
        this.send();
    }

    onSubmit(event: Event): void {
        event.preventDefault();
        this.send();
    }

    ngAfterViewChecked(): void {
        if (this.shouldScroll && this.scrollRef) {
            this.scrollRef.nativeElement.scrollTop = this.scrollRef.nativeElement.scrollHeight;
            this.shouldScroll = false;
        }
    }

    private async send(): Promise<void> {
        const text = this.draft.trim();
        if (!text || this.sending || !this.companyId) return;

        this.messages = [...this.messages, { role: 'user', content: text, timestamp: new Date() }];
        this.draft = '';
        this.sending = true;
        this.statusText = 'Thinking…';
        this.shouldScroll = true;
        this.cdr.detectChanges();

        try {
            const agentId = await this.resolveAgentId();
            if (!agentId) {
                this.appendError(`Could not find agent "${SUPPORT_AGENT_NAME}". Has metadata been synced?`);
                return;
            }

            const gql = Metadata.Provider as GraphQLDataProvider;
            const conversation = this.messages.map(m => ({ role: m.role, content: m.content }));

            this.statusText = 'Querying data…';
            this.cdr.detectChanges();

            const result = await gql.ExecuteGQL(
                `mutation RunAIAgent($agentId: String!, $sessionId: String!, $messages: String!, $payload: String, $fireAndForget: Boolean) {
                    RunAIAgent(agentId: $agentId, sessionId: $sessionId, messages: $messages, payload: $payload, fireAndForget: $fireAndForget) { success errorMessage result }
                }`,
                {
                    agentId,
                    sessionId: this.sessionId,
                    messages: JSON.stringify(conversation),
                    payload: JSON.stringify({
                        companyId: this.companyId,
                        brandName: this.brandName || '',
                        currentDate: new Date().toISOString().split('T')[0],
                    }),
                    fireAndForget: false,
                }
            );
            const response = result?.RunAIAgent;
            if (!response?.success) {
                this.appendError(response?.errorMessage || 'Agent run failed.');
                return;
            }

            const reply = this.extractReply(response.result);
            this.messages = [...this.messages, {
                role: 'assistant',
                content: reply,
                rendered: this.renderMarkdown(reply),
                timestamp: new Date(),
            }];
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            this.appendError(msg);
        } finally {
            this.sending = false;
            this.shouldScroll = true;
            this.cdr.detectChanges();
        }
    }

    private async resolveAgentId(): Promise<string | null> {
        if (this.agentId) return this.agentId;
        const rv = new RunView();
        const result = await rv.RunView<{ ID: string }>({
            EntityName: 'MJ: AI Agents',
            ExtraFilter: `Name='${SUPPORT_AGENT_NAME}'`,
            ResultType: 'simple',
            Fields: ['ID'],
            MaxRows: 1,
        });
        if (result.Success && result.Results.length > 0) {
            this.agentId = result.Results[0].ID;
            return this.agentId;
        }
        const reason = result.Success ? 'no rows returned' : (result.ErrorMessage || 'query failed');
        LogError(`[CMOSupportChat] resolveAgentId failed for '${SUPPORT_AGENT_NAME}': ${reason}`);
        return null;
    }

    private extractReply(rawResult: unknown): string {
        if (!rawResult) return '(no response)';
        const text = typeof rawResult === 'string' ? rawResult : JSON.stringify(rawResult);
        try {
            const parsed = JSON.parse(text);
            if (typeof parsed === 'string') return parsed;
            if (parsed?.message) return String(parsed.message);
            if (parsed?.content) return String(parsed.content);
            if (parsed?.response) return String(parsed.response);
            if (parsed?.reply) return String(parsed.reply);
            if (parsed?.taskComplete && parsed?.summary) return String(parsed.summary);
            return text;
        } catch {
            return text;
        }
    }

    private renderMarkdown(text: string): SafeHtml {
        const html = marked.parse(text, { async: false }) as string;
        return this.sanitizer.sanitize(SecurityContext.HTML, html) ?? '';
    }

    private appendError(msg: string): void {
        this.messages = [...this.messages, {
            role: 'assistant',
            content: msg,
            timestamp: new Date(),
            error: true,
        }];
    }
}
